import { CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../environments/environment';
import { SnackbarService } from '@zen/ui/services/snackbar.service';
import { AuthService } from './studio-tools/services/auth-service';
import { StudioCommonService } from './studio-tools/services/studio-common.service';
import { EnvService } from '@zb/services/src/services/env.service';

function isMarketplaceUrl(url: string): boolean {
  return url.includes('/marketplace');
}

function isConsoleUrl(url: string): boolean {
  return url.includes('/console');
}

export const studioRouteGuard: CanActivateFn = async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const platformId = inject(PLATFORM_ID);                     // ← add

  // ✅ SSG/SSR prerender — skip all browser APIs, allow access
  if (!isPlatformBrowser(platformId)) {
    return true;
  }
  const router = inject(Router);
  const snackBarService = inject(SnackbarService);
  const studioCommonService = inject(StudioCommonService);
  const envService = inject(EnvService);
  const authService = inject(AuthService);
  authService.authenticationLoader.set(true);
  const queryParams = route.queryParams;
  const isDevMode = queryParams['dev'] === 'true';

  // if (isMarketplaceUrl(state.url) && !isDevMode) {
  //   await envService.setEnvironment({info:'US'});
  //   authService.authenticationLoader.set(false);
  //   return true;
  // }

  let pageLoadType!: 'reload' | 'navigate';

  const [navEntry] = performance.getEntriesByType("navigation");
  const nav = navEntry as PerformanceNavigationTiming;

  if (nav.type === "navigate") {
    pageLoadType = 'navigate';
  } else if (nav.type === "reload") {
    pageLoadType = 'reload';
  }

  const isOpenedInEditor = typeof window !== 'undefined' && window.parent !== window;
  if (isMarketplaceUrl(state.url) && (window != undefined && !window.opener) && !isOpenedInEditor && !isDevMode) {
    const region = envService?.getEnvironment()?.region;
    await envService.setEnvironment({ info: region });
    authService.authenticationLoader.set(false);
    return true;
  }


  return isDevMode || isConsoleUrl(state.url)
    ? await handleDevMode(router, snackBarService, authService, studioCommonService, envService, pageLoadType)
    : requestTokenFromOpener(snackBarService, authService, studioCommonService, envService, pageLoadType, state.url);
};


// ----------------- Helper Methods ----------------------

async function handleDevMode(router: Router, snackBarService: SnackbarService, authService: AuthService, studioCommonService: StudioCommonService, envService: EnvService, pageLoadType: 'reload' | 'navigate'): Promise<boolean> {
  // const devToken = localStorage.getItem('dev_mode_token');
  const devToken = studioCommonService.getCookie('dev_mode_token');

  // const region = localStorage.getItem('region');
  const region = typeof window !== 'undefined' ? localStorage.getItem('region') : null;

  if (devToken) {
    console.info('[StudioGuard] In Dev Mode & dev_mode_token found, access allowed ✅');
    authService.authenticationLoader.set(false);
    if (region) {
      await envService.domainChecking(region);
    }
    return true;
  } else {
    console.warn('[StudioGuard] In Dev Mode but dev_mode_token not found, redirecting to /auth/signin 🚪');
    snackBarService.showSnackBar({ actionType: "error", prefixIcon: 'info', message: 'Session expired. Please sign in.', durationTime: 3, horizontalPosition: 'center' });
    setTimeout(() => authService.authenticationLoader.set(false), 1000);
    router.navigate(['/auth/signin']);
    return false;
  }
}

function requestTokenFromOpener(snackBarService: SnackbarService, authService: AuthService, studioCommonService: StudioCommonService, envService: EnvService, pageLoadType: 'reload' | 'navigate', url: string): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    if (typeof window === 'undefined') {
      resolve(true);
      return;
    }

    if (window.opener && pageLoadType === 'navigate') {
      window.opener.postMessage('requestToken', environment.zenbasketUrl);
    }

    const jwtToken = localStorage.getItem('jwt_token');
    let region = localStorage.getItem('region');
    let resolved = false; // 👈 prevent multiple resolves
    const handler = async (event: MessageEvent) => {
      if (event.origin !== environment.zenbasketUrl) return;
      const receivedToken = event.data?.token;
      region = event.data?.region;
      if (receivedToken) {
        localStorage.setItem('jwt_token', receivedToken);
        localStorage.removeItem('getCurrentUser');
        if (region) {
          localStorage.setItem('region', region);
          await envService.domainChecking(region);
        }
        // commonService.setCookie('jwt_token', receivedToken, 86400);
        console.info('[StudioGuard] Token received via postMessage, access allowed ✅');
        cleanup();
        authService.authenticationLoader.set(false);
        resolved = true;
        resolve(true);
      }
    };

    const cleanup = () => {
      window.removeEventListener('message', handler);
      clearTimeout(timeoutRef);
    };

    window.addEventListener('message', handler);

    const timeoutRef = setTimeout(async () => {
      if (!resolved) {
        console.warn('[StudioGuard] Token request timed out, checking localStorage...');
        cleanup();
        if (region) {
          await envService.domainChecking(region);
        }
        if (jwtToken) {
          console.log('[StudioGuard] Using existing token from localStorage ✅');
          authService.authenticationLoader.set(false);
          resolve(true);
        } else if (isMarketplaceUrl(url)) {
          console.info('[StudioGuard] Marketplace route — allowing access without sign-in redirect');
          authService.authenticationLoader.set(false);
          resolve(true);
        } else {
          console.warn('[StudioGuard] No token found, redirecting to signin 🚪');
          redirectToSignIn(snackBarService);
          setTimeout(() => authService.authenticationLoader.set(false), 1000);
          resolve(false);
        }
      }
    }, pageLoadType === 'navigate' ? 3000 : 0);
  });
}


function redirectToSignIn(snackBarService: SnackbarService): void {
  if (typeof window === 'undefined') return;
  snackBarService.showSnackBar({ actionType: "error", prefixIcon: 'info', message: 'Session expired. Please sign in.', durationTime: 3, horizontalPosition: 'center' });
  window.location.href = `${environment.zenbasketUrl}/signin`;
}
